// R-CONNECT — shared connect summary for the «شبکه» nav item badge.
//
// Mirrors useNotifications / useChatUnread: the Nav is mounted for the whole
// authed session, so this fetches the caller's INCOMING connection requests
// once on mount, then keeps the badge fresh with a slow ~30s poll (a cheap
// fallback that also catches requests that arrived while the tab was
// backgrounded). It exposes the count for the nav badge + a `refresh` the
// Network page calls after an accept/decline so the badge clears immediately.
// Anonymous → 0 + no fetch. Fully best-effort: a failed fetch keeps the last
// known value (the badge just doesn't update) — it never blocks the nav.
import React from "react";

import { useAuth } from "../auth/AuthContext";
import { connectApi } from "../api/endpoints-core.js";

interface IncomingRequestRow {
  connectionId: string;
  userId: string;
}

export interface UseConnectSummary {
  /** Number of pending INCOMING connection requests (drives the nav badge). */
  pendingIncoming: number;
  loading: boolean;
  refresh: () => Promise<void>;
}

const POLL_MS = 30000;

export const useConnectSummary = (): UseConnectSummary => {
  const { isAuthenticated } = useAuth();
  const [pendingIncoming, setPendingIncoming] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  const refresh = React.useCallback(async () => {
    if (!isAuthenticated) {
      setPendingIncoming(0);
      return;
    }
    setLoading(true);
    try {
      const rows = (await connectApi.incoming()) as IncomingRequestRow[] | null;
      setPendingIncoming(Array.isArray(rows) ? rows.length : 0);
    } catch {
      // best-effort — keep the last known value (don't zero the badge on a blip).
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Initial fetch + slow poll fallback (cleared on logout / unmount).
  React.useEffect(() => {
    if (!isAuthenticated) {
      setPendingIncoming(0);
      return;
    }
    void refresh();
    const t = setInterval(() => void refresh(), POLL_MS);
    return () => clearInterval(t);
  }, [isAuthenticated, refresh]);

  return { pendingIncoming, loading, refresh };
};
